import { callable } from './callable'
import { uploadWorkspaceFiles } from './storageHubService'
import type { ChecklistAttachment } from '../types/schema'

// ---------------------------------------------------------------------------
// 1. Metric definitions types & interfaces (EQ173)
// ---------------------------------------------------------------------------

export type EduProgressMetricKind = 'NUMBER' | 'DURATION_SEC' | 'COUNT' | 'PASS_FAIL'
export type EduProgressMetricDirection = 'HIGHER_BETTER' | 'LOWER_BETTER'

export interface EduProgressMetric {
  metricId: string
  workspaceId: string
  rootId: string
  name: string
  unit?: string
  kind: EduProgressMetricKind
  direction: EduProgressMetricDirection
  target?: number | null
  selfLoggable: boolean
  archived: boolean
  order: number
  version: number
  createdAt?: unknown
  createdBy?: string
  updatedAt?: unknown
  updatedBy?: string
}

export interface SetEduProgressMetricInput {
  workspaceId: string
  rootId: string
  metricId?: string
  name: string
  unit?: string
  kind: EduProgressMetricKind
  direction: EduProgressMetricDirection
  target?: number | null
  selfLoggable?: boolean
  archived?: boolean
  order?: number
  expectedVersion?: number
}

export interface SetEduProgressMetricResult {
  success: boolean
  metricId?: string
  metric: EduProgressMetric
}

export interface ListEduProgressMetricsInput {
  workspaceId: string
  rootId: string
  includeArchived?: boolean
}

export interface ListEduProgressMetricsResult {
  metrics: EduProgressMetric[]
}

// ---------------------------------------------------------------------------
// 2. Metric values & self-reports types & interfaces (EQ173)
// ---------------------------------------------------------------------------

export type EduProgressValueSource = 'STAFF' | 'SELF'
export type EduProgressValueStatus = 'CONFIRMED' | 'SELF_REPORTED' | 'REJECTED'

export interface EduProgressValue {
  id: string
  workspaceId: string
  rootId: string
  metricId: string
  learnerId: string
  value: number
  recordedAt: string
  source: EduProgressValueSource
  status: EduProgressValueStatus
  recordedBy: string
  confirmedBy?: string | null
  confirmedAt?: unknown | null
  rejectedBy?: string | null
  rejectedAt?: unknown | null
  rejectReason?: string | null
  note?: string | null
  sessionKey?: string | null
  requestId?: string | null
  createdAt?: unknown
}

export interface SetEduProgressValuesInput {
  workspaceId: string
  rootId: string
  metricId: string
  date: string
  entries: Array<{
    learnerId: string
    value: number
    note?: string | null
    sessionKey?: string | null
  }>
  requestId?: string
}

export interface SetEduProgressValuesResult {
  success: boolean
  count: number
  idempotent?: boolean
}

export interface ConfirmEduProgressValuesInput {
  workspaceId: string
  rootId: string
  valueIds: string[]
}

export interface ConfirmEduProgressValuesResult {
  success: boolean
  count: number
}

export interface RejectEduProgressValuesInput {
  workspaceId: string
  rootId: string
  valueIds: string[]
  reason: string
}

export interface RejectEduProgressValuesResult {
  success: boolean
  count: number
}

export interface LogEduProgressValueInput {
  workspaceId: string
  rootId: string
  metricId: string
  learnerId: string
  value: number
  date: string
  note?: string | null
}

export interface LogEduProgressValueResult {
  success: boolean
  valueId: string
}

export interface ListEduPendingSelfReportsInput {
  workspaceId: string
  rootId: string
  cursor?: string
  limit?: number
}

export interface EduPendingSelfReportItem {
  id: string
  learnerId: string
  learnerName: string
  metricId: string
  metricName: string
  metricUnit: string
  metricKind: EduProgressMetricKind
  value: number
  recordedAt: string
  note: string | null
  recordedBy: string
  loggedByName: string
}

export interface ListEduPendingSelfReportsResult {
  items: EduPendingSelfReportItem[]
  nextCursor: string | null
}

export type EduPendingSelfReport = EduPendingSelfReportItem

// ---------------------------------------------------------------------------
// 3. Levels & Promotion types & interfaces (EQ174)
// ---------------------------------------------------------------------------

export interface EduLevelCriterionMetricTarget {
  metricId: string
  value: number
}

export interface EduLevelCriteria {
  minAttendancePercent?: number
  metricTargets?: EduLevelCriterionMetricTarget[]
  minDaysAtPrevious?: number
}

export interface EduProgressLevel {
  levelId: string
  name: string
  colour: string
  criteria: EduLevelCriteria
  certificateTemplateId?: string | null
  order: number
}

export interface EduProgressLevelsDoc {
  workspaceId: string
  rootId: string
  version: number
  levels: EduProgressLevel[]
  updatedBy: string
  updatedAt?: unknown
}

export interface SetEduProgressLevelsInput {
  workspaceId: string
  rootId: string
  levels: EduProgressLevel[]
  expectedVersion?: number
}

export interface SetEduProgressLevelsResult {
  success: boolean
  version: number
  levels: EduProgressLevel[]
}

export interface GetEduProgressLevelsInput {
  workspaceId: string
  rootId: string
}

export interface GetEduProgressLevelsResult {
  version: number
  levels: EduProgressLevel[]
  updatedAt?: unknown
}

export interface EduPromotionHistoryEntry {
  fromLevelId: string | null
  toLevelId: string
  date: string
  by: string
  note?: string | null
  criteriaSnapshot: Record<string, { met: boolean; target?: unknown; actual?: unknown }>
}

export interface EduLevelCriteriaEvaluation {
  attendance?: {
    met: boolean
    minPercent: number
    actualPercent: number | null
  }
  metricTargets?: Array<{
    metricId: string
    metricName?: string
    target: number
    actual: number | null
    direction: EduProgressMetricDirection
    met: boolean
  }>
  minDaysAtPrevious?: {
    met: boolean
    minDays: number
    actualDays: number | null
  }
  allMet: boolean
  snapshot: Record<string, { met: boolean; target?: unknown; actual?: unknown }>
}

export interface PromoteEduLearnerLevelInput {
  workspaceId: string
  rootId: string
  learnerId: string
  toLevelId: string
  date?: string
  note?: string | null
}

export interface PromoteEduLearnerLevelResult {
  success: boolean
  currentLevelId: string
  criteriaSnapshot: Record<string, { met: boolean; target?: unknown; actual?: unknown }>
  allMet: boolean
  certificateId: string | null
  code?: string
}

export interface GetEduLearnerProgressInput {
  workspaceId: string
  rootId: string
  learnerId: string
}

export interface GetEduLearnerProgressResult {
  metrics: Array<{
    metricId: string
    name: string
    unit?: string
    kind: EduProgressMetricKind
    direction: EduProgressMetricDirection
    target?: number | null
    selfLoggable: boolean
    series: Array<{
      id: string
      date: string
      value: number
      status: EduProgressValueStatus
      source: EduProgressValueSource
      note?: string | null
    }>
  }>
  currentLevel: {
    levelId: string
    name: string
    colour: string
    order: number
    since: string
  } | null
  history: EduPromotionHistoryEntry[]
  nextLevel: {
    levelId: string
    name: string
    colour: string
    order: number
    criteriaEvaluation: EduLevelCriteriaEvaluation
  } | null
}

export type EduLearnerProgressMetricItem = GetEduLearnerProgressResult['metrics'][number]

export interface ListEduProgramProgressInput {
  workspaceId: string
  rootId: string
}

export interface ListEduProgramProgressResult {
  learners: Array<{
    learnerId: string
    displayName: string
    currentLevel: {
      levelId: string
      name: string
      colour: string
      order: number
    } | null
    latestValues: Record<string, { value: number; date: string; status: EduProgressValueStatus }>
    pendingSelfReportsCount: number
  }>
  totalPendingSelfReports: number
}

export type EduProgramLearnerProgress = ListEduProgramProgressResult['learners'][number]

// ---------------------------------------------------------------------------
// 4. Certificates types & interfaces (EQ175, EQ181)
// ---------------------------------------------------------------------------

export interface EduCertTemplate {
  id: string
  workspaceId: string
  name: string
  title: string
  body: string
  validityMonths: number | null
  archived: boolean
  version: number
  createdBy: string
  updatedBy: string
  createdAt?: unknown
  updatedAt?: unknown
}

export interface SetEduCertTemplateInput {
  workspaceId: string
  templateId?: string
  name: string
  title: string
  body: string
  validityMonths?: number | null
  archived?: boolean
  expectedVersion?: number
}

export interface SetEduCertTemplateResult {
  success: boolean
  templateId: string
  template: EduCertTemplate
}

export interface ListEduCertTemplatesInput {
  workspaceId: string
  includeArchived?: boolean
}

export interface ListEduCertTemplatesResult {
  templates: EduCertTemplate[]
}

export interface IssueEduCertificateInput {
  workspaceId: string
  rootId: string
  learnerId: string
  templateId: string
  levelId?: string | null
  issueDate?: string
  requestId?: string
}

export interface IssueEduCertificateResult {
  success: boolean
  certificateId: string
  code: string
  issueDate?: string
  expiryDate?: string | null
  idempotent?: boolean
}

export interface ListEduCertificatesInput {
  workspaceId: string
  rootId?: string
  learnerId?: string
}

export interface EduCertificateSnapshot {
  holder: {
    displayName: string
    isMinor: boolean
  }
  programme: {
    title: string
  }
  level: {
    name: string
  } | null
  rendered: {
    title: string
    body: string
  }
  workspace: {
    name: string
    logoUrl: string | null
    timezone?: string
  }
  signer: {
    name: string
    title: string
    signaturePath: string | null
  }
  timezone?: string
}

export interface EduCertificateItem {
  id: string
  workspaceId: string
  rootId: string
  learnerId: string
  templateId: string
  levelId: string | null
  source: 'AUTO' | 'MANUAL'
  status: 'ISSUED' | 'REVOKED' | 'SUPERSEDED'
  issueDate: string
  expiryDate: string | null
  issuedBy: string
  code: string
  pdfStatus: 'PENDING' | 'READY' | 'FAILED'
  pdfPath: string | null
  pdfError?: string | null
  snapshot: EduCertificateSnapshot
  revokedReason?: string | null
  revokedAt?: unknown | null
  revokedBy?: string | null
  supersedes?: string | null
  supersededBy?: string | null
  requestId?: string | null
  expiryRemindersSent?: Record<string, string>
  createdAt?: unknown
  updatedAt?: unknown
}

export interface ListEduCertificatesResult {
  certificates: EduCertificateItem[]
}

export interface GetEduCertificatePdfUrlInput {
  workspaceId: string
  certificateId: string
}

export interface GetEduCertificatePdfUrlResult {
  url: string | null
}

export interface RevokeEduCertificateInput {
  workspaceId: string
  certificateId: string
  reason: string
}

export interface RevokeEduCertificateResult {
  success: boolean
  certificateId: string
  status?: 'REVOKED'
}

export interface RenewEduCertificateInput {
  workspaceId: string
  certificateId: string
}

export interface RenewEduCertificateResult {
  success: boolean
  certificateId: string
  code: string
  oldCertificateId?: string
  newCertificateId?: string
  newCode?: string
  issueDate?: string
  expiryDate?: string | null
}

export interface VerifyEduCertificateInput {
  code: string
  workspaceId?: string
}

export type EduCertificateVerificationStatus = 'VALID' | 'EXPIRED' | 'REVOKED' | 'REPLACED'

export interface VerifyEduCertificateResult {
  title: string
  holder: string
  programme: string
  issueDate: string
  expiryDate: string | null
  status: EduCertificateVerificationStatus
  workspaceName: string
  demoNotice?: string
}

// ---------------------------------------------------------------------------
// 5. Practice Logs types & interfaces (EQ176)
// ---------------------------------------------------------------------------

export interface EduPracticeLogComment {
  id: string
  by: string
  byName: string
  text: string
  at: string
}

export interface EduPracticeLog {
  id: string
  workspaceId: string
  rootId: string
  learnerId: string
  date: string
  minutes: number
  practised: string
  notes: string | null
  source: 'MANUAL' | 'FOCUS'
  focusSessionId: string | null
  attachments: ChecklistAttachment[]
  metricValueIds: string[]
  comments: EduPracticeLogComment[]
  createdBy: string
  createdAt: unknown
  updatedAt?: unknown
  deletedAt: unknown | null
  requestId?: string | null
}

export interface ListEduPracticeLogsInput {
  workspaceId: string
  rootId?: string
  learnerId?: string
  cursor?: string
  limit?: number
}

export interface ListEduPracticeLogsResult {
  logs: EduPracticeLog[]
  nextCursor: string | null
}

export interface AddEduPracticeLogCommentInput {
  workspaceId: string
  logId: string
  text: string
}

export interface AddEduPracticeLogCommentResult {
  success: boolean
  comment: EduPracticeLogComment
}

export interface CreateEduPracticeLogInput {
  workspaceId: string
  rootId: string
  learnerId: string
  date: string
  minutes: number
  practised: string
  notes?: string | null
  fileIds?: string[]
  metricValues?: Array<{ metricId: string; value: unknown }>
  requestId?: string
}

export interface CreateEduPracticeLogResult {
  success: boolean
  logId: string
  log: EduPracticeLog
}

export interface UpdateEduPracticeLogInput {
  workspaceId: string
  logId: string
  minutes?: number
  practised?: string
  notes?: string | null
  fileIds?: string[]
}

export interface UpdateEduPracticeLogResult {
  success: boolean
  logId: string
}

export interface DeleteEduPracticeLogInput {
  workspaceId: string
  logId: string
}

export interface DeleteEduPracticeLogResult {
  success: boolean
  logId: string
}

export interface RecordEduFocusPracticeLogInput {
  workspaceId: string
  rootId: string
  learnerId: string
  focusSessionId: string
  startedAt: string | number
  minutes: number
  practised?: string
  notes?: string | null
}

export interface RecordEduFocusPracticeLogResult {
  success: boolean
  logId: string
  log: EduPracticeLog
}

export interface UploadEduPracticeLogFilesInput {
  workspaceId: string
  rootId: string
  learnerId: string
  files: File[]
  onProgress?: (completed: number, total: number, fileName: string) => void
}

// ---------------------------------------------------------------------------
// Callable Wrappers
// ---------------------------------------------------------------------------

export const setEduProgressMetric = async (
  input: SetEduProgressMetricInput,
): Promise<SetEduProgressMetricResult> =>
  callable<SetEduProgressMetricInput, SetEduProgressMetricResult>('setEduProgressMetric', input)

export const listEduProgressMetrics = async (
  input: ListEduProgressMetricsInput,
): Promise<ListEduProgressMetricsResult> =>
  callable<ListEduProgressMetricsInput, ListEduProgressMetricsResult>('listEduProgressMetrics', input)

export const setEduProgressValues = async (
  input: SetEduProgressValuesInput,
): Promise<SetEduProgressValuesResult> =>
  callable<SetEduProgressValuesInput, SetEduProgressValuesResult>('setEduProgressValues', input)

export const confirmEduProgressValues = async (
  input: ConfirmEduProgressValuesInput,
): Promise<ConfirmEduProgressValuesResult> =>
  callable<ConfirmEduProgressValuesInput, ConfirmEduProgressValuesResult>('confirmEduProgressValues', input)

export const rejectEduProgressValues = async (
  input: RejectEduProgressValuesInput,
): Promise<RejectEduProgressValuesResult> =>
  callable<RejectEduProgressValuesInput, RejectEduProgressValuesResult>('rejectEduProgressValues', input)

export const listEduPendingSelfReports = async (
  input: ListEduPendingSelfReportsInput,
): Promise<ListEduPendingSelfReportsResult> =>
  callable<ListEduPendingSelfReportsInput, ListEduPendingSelfReportsResult>('listEduPendingSelfReports', input)

export const setEduProgressLevels = async (
  input: SetEduProgressLevelsInput,
): Promise<SetEduProgressLevelsResult> =>
  callable<SetEduProgressLevelsInput, SetEduProgressLevelsResult>('setEduProgressLevels', input)

export const getEduProgressLevels = async (
  input: GetEduProgressLevelsInput,
): Promise<GetEduProgressLevelsResult> =>
  callable<GetEduProgressLevelsInput, GetEduProgressLevelsResult>('getEduProgressLevels', input)

export const promoteEduLearnerLevel = async (
  input: PromoteEduLearnerLevelInput,
): Promise<PromoteEduLearnerLevelResult> =>
  callable<PromoteEduLearnerLevelInput, PromoteEduLearnerLevelResult>('promoteEduLearnerLevel', input)

export const getEduLearnerProgress = async (
  input: GetEduLearnerProgressInput,
): Promise<GetEduLearnerProgressResult> =>
  callable<GetEduLearnerProgressInput, GetEduLearnerProgressResult>('getEduLearnerProgress', input)

export const listEduProgramProgress = async (
  input: ListEduProgramProgressInput,
): Promise<ListEduProgramProgressResult> =>
  callable<ListEduProgramProgressInput, ListEduProgramProgressResult>('listEduProgramProgress', input)

export const setEduCertTemplate = async (
  input: SetEduCertTemplateInput,
): Promise<SetEduCertTemplateResult> =>
  callable<SetEduCertTemplateInput, SetEduCertTemplateResult>('setEduCertTemplate', input)

export const listEduCertTemplates = async (
  input: ListEduCertTemplatesInput,
): Promise<ListEduCertTemplatesResult> =>
  callable<ListEduCertTemplatesInput, ListEduCertTemplatesResult>('listEduCertTemplates', input)

export const issueEduCertificate = async (
  input: IssueEduCertificateInput,
): Promise<IssueEduCertificateResult> =>
  callable<IssueEduCertificateInput, IssueEduCertificateResult>('issueEduCertificate', input)

export const listEduCertificates = async (
  input: ListEduCertificatesInput,
): Promise<ListEduCertificatesResult> =>
  callable<ListEduCertificatesInput, ListEduCertificatesResult>('listEduCertificates', input)

export const getEduCertificatePdfUrl = async (
  input: GetEduCertificatePdfUrlInput,
): Promise<GetEduCertificatePdfUrlResult> =>
  callable<GetEduCertificatePdfUrlInput, GetEduCertificatePdfUrlResult>('getEduCertificatePdfUrl', input)

export const revokeEduCertificate = async (
  input: RevokeEduCertificateInput,
): Promise<RevokeEduCertificateResult> =>
  callable<RevokeEduCertificateInput, RevokeEduCertificateResult>('revokeEduCertificate', input)

export const renewEduCertificate = async (
  input: RenewEduCertificateInput,
): Promise<RenewEduCertificateResult> =>
  callable<RenewEduCertificateInput, RenewEduCertificateResult>('renewEduCertificate', input)

export const listEduPracticeLogs = async (
  input: ListEduPracticeLogsInput,
): Promise<ListEduPracticeLogsResult> =>
  callable<ListEduPracticeLogsInput, ListEduPracticeLogsResult>('listEduPracticeLogs', input)

export const addEduPracticeLogComment = async (
  input: AddEduPracticeLogCommentInput,
): Promise<AddEduPracticeLogCommentResult> =>
  callable<AddEduPracticeLogCommentInput, AddEduPracticeLogCommentResult>('addEduPracticeLogComment', input)

export const createEduPracticeLog = async (
  input: CreateEduPracticeLogInput,
): Promise<CreateEduPracticeLogResult> =>
  callable<CreateEduPracticeLogInput, CreateEduPracticeLogResult>('createEduPracticeLog', input)

export const updateEduPracticeLog = async (
  input: UpdateEduPracticeLogInput,
): Promise<UpdateEduPracticeLogResult> =>
  callable<UpdateEduPracticeLogInput, UpdateEduPracticeLogResult>('updateEduPracticeLog', input)

export const deleteEduPracticeLog = async (
  input: DeleteEduPracticeLogInput,
): Promise<DeleteEduPracticeLogResult> =>
  callable<DeleteEduPracticeLogInput, DeleteEduPracticeLogResult>('deleteEduPracticeLog', input)

export const logEduProgressValue = async (
  input: LogEduProgressValueInput,
): Promise<LogEduProgressValueResult> =>
  callable<LogEduProgressValueInput, LogEduProgressValueResult>('logEduProgressValue', input)

export const recordEduFocusPracticeLog = async (
  input: RecordEduFocusPracticeLogInput,
): Promise<RecordEduFocusPracticeLogResult> =>
  callable<RecordEduFocusPracticeLogInput, RecordEduFocusPracticeLogResult>('recordEduFocusPracticeLog', input)

export const verifyEduCertificate = async (
  input: VerifyEduCertificateInput,
): Promise<VerifyEduCertificateResult> =>
  callable<VerifyEduCertificateInput, VerifyEduCertificateResult>('verifyEduCertificate', input)

export async function uploadEduPracticeLogFiles(
  input: UploadEduPracticeLogFilesInput,
): Promise<string[]> {
  if (input.files.length === 0) return []
  const results = await uploadWorkspaceFiles({
    workspaceId: input.workspaceId,
    files: input.files,
    visibility: 'PRIVATE',
    artifactType: 'EDU_PRACTICE_LOG',
    artifactId: `${input.rootId}_${input.learnerId}`,
    onProgress: input.onProgress,
  })
  const fileIds: string[] = []
  for (const res of results) {
    if (!res.fileId) {
      throw new Error('Upload completed without returning a file ID.')
    }
    fileIds.push(res.fileId)
  }
  return fileIds
}

